/**
 * Single notification row used in both the bell dropdown and the
 * `/notifications` page. Pure presentation — all interactions are passed in
 * as callbacks.
 */
import { formatDistanceToNow } from "date-fns";
import { de as deLocale, enUS, fr as frLocale, it as itLocale } from "date-fns/locale";

import { useI18n } from "@/i18n";
import { cn } from "@/lib/utils";
import type { NotificationInboxItem } from "@/services/notifications";
import {
  getNotificationPresentation,
  TONE_TEXT_CLASS,
} from "./notificationPresentation";

const LOCALE_MAP = { de: deLocale, en: enUS, fr: frLocale, it: itLocale } as const;

interface Props {
  item: NotificationInboxItem;
  onOpen?: (item: NotificationInboxItem) => void;
  compact?: boolean;
}

export const NotificationItemRow = ({ item, onOpen, compact = false }: Props) => {
  const { t, locale } = useI18n();
  const presentation = getNotificationPresentation(item);
  const Icon = presentation.icon;
  const isUnread = item.status === "unread";

  let timeLabel = "";
  try {
    timeLabel = formatDistanceToNow(new Date(item.created_at), {
      addSuffix: true,
      locale: LOCALE_MAP[locale],
    });
  } catch {
    timeLabel = item.created_at;
  }

  return (
    <button
      type="button"
      onClick={() => onOpen?.(item)}
      className={cn(
        "w-full text-left flex gap-3 rounded-lg px-3 py-2.5 transition-colors",
        "hover:bg-muted/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        isUnread && "bg-primary/[0.04]",
        compact ? "items-start" : "items-start",
      )}
      aria-label={item.title}
    >
      <span
        className={cn(
          "mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted/70",
          TONE_TEXT_CLASS[presentation.tone],
        )}
        aria-hidden="true"
      >
        <Icon className="h-4 w-4" strokeWidth={1.75} />
      </span>

      <span className="flex-1 min-w-0">
        <span className="flex items-center gap-2">
          <span
            className={cn(
              "truncate text-sm",
              isUnread ? "font-semibold text-foreground" : "font-medium text-foreground/90",
            )}
          >
            {item.title}
          </span>
          {isUnread && (
            <span
              className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
              aria-label={t("notifications.unread")}
            />
          )}
        </span>
        {item.body && (
          <span
            className={cn(
              "mt-0.5 block text-xs text-muted-foreground",
              compact ? "line-clamp-2" : "line-clamp-3",
            )}
          >
            {item.body}
          </span>
        )}
        <span className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground/80">
          <span className="rounded-full bg-muted/60 px-1.5 py-0.5 uppercase tracking-wide">
            {t(presentation.labelKey)}
          </span>
          <span>·</span>
          <span>{timeLabel}</span>
        </span>
      </span>
    </button>
  );
};